import React, { useCallback } from 'react';

import { DateHelpers } from '@shared/helpers';

import { PERIOD_SEPARATOR } from '../../model/consts';
import { autoFormatDate, normalizeDate } from '../helpers';

interface UseChangePeriodDateProps {
    value: string;
    setValue: (v: string) => void;
    minDate?: Date;
    maxDate?: Date;
}

export const useChangePeriodDate = ({
    value,
    setValue,
    minDate = DateHelpers.getMinDate(),
    maxDate = DateHelpers.getToday(),
}: UseChangePeriodDateProps) => {
    const { dateFormatter, parsePeriodString, toDate, getDateRangeStatus } = DateHelpers;

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value;
            const prev = value || '';
            const isDeletion = raw.length < prev.length;

            const [sRaw, eRaw = ''] = raw.split('-').map((p) => p.trim());

            const sFmt = autoFormatDate(sRaw, isDeletion);
            const eFmt = autoFormatDate(eRaw, isDeletion);

            const sNorm = normalizeDate(sFmt, minDate, maxDate);
            const eNorm = normalizeDate(eFmt, minDate, maxDate);

            let formatted = sNorm;
            const single = !raw.includes('-') && sNorm.length === 10 && !eNorm;
            if (!isDeletion && single) {
                const status = getDateRangeStatus(sNorm, minDate, maxDate);
                if (status === 'before') formatted = dateFormatter({ date: minDate });
                else if (status === 'after') formatted = dateFormatter({ date: maxDate });
                formatted += PERIOD_SEPARATOR;
            } else if (raw.includes('-') || eNorm) {
                formatted += PERIOD_SEPARATOR + eNorm;
            }
            if (!sNorm && !eNorm) formatted = '';

            const [startDate, endDate] = parsePeriodString(formatted);

            if (startDate && endDate && toDate(endDate) < toDate(startDate)) {
                formatted = `${sNorm}${PERIOD_SEPARATOR}${sNorm}`;
            }
            setValue(formatted);
        },
        [
            value,
            minDate,
            maxDate,
            dateFormatter,
            getDateRangeStatus,
            parsePeriodString,
            toDate,
            setValue,
        ]
    );

    return {
        value,
        handleChange,
    };
};
